import { z } from 'zod';

import { UuidSchema } from '../common/id/id';
import { makeListQuery } from '../common/list-query/list-query';
import { makeTranslationsSchema } from '../common/translations/translations';

import { BooleanParamSchema } from '../common/boolean-param/boolean-param';

import { QuestionFlowSchema } from './question-flow/question-flow';
import { StableKeySchema } from './stable-key/stable-key';

/**
 * A group of questions rendered together — one step of the registration flow or
 * one page of the profile wizard, depending on its `flow`.
 *
 * Sections are what make the flows *feel* designed rather than a wall of
 * fields: staff decide what belongs together, in what order, and under what
 * heading, without a deploy.
 */
export const QuestionSectionSchema = z
  .object({
    id: UuidSchema,
    key: StableKeySchema,
    flow: QuestionFlowSchema,
    sortOrder: z.number().int().min(0),
    isActive: z.boolean(),
    /** The active locale's copy. */
    title: z.string(),
    description: z.string().nullable(),
  })
  .strict();

export type QuestionSection = z.infer<typeof QuestionSectionSchema>;

export const QuestionSectionTranslationsSchema = makeTranslationsSchema(
  z
    .object({
      title: z.string().min(1).max(200),
      description: z.string().max(2_000).nullable(),
    })
    .strict(),
);

export type QuestionSectionTranslations = z.infer<typeof QuestionSectionTranslationsSchema>;

/** A section plus every locale — what the builder's edit form prefills from (W7). */
export const QuestionSectionDetailSchema = QuestionSectionSchema.extend({
  translations: QuestionSectionTranslationsSchema,
  /** How many questions it holds, so the builder can warn before deactivating a full section. */
  questionCount: z.number().int().min(0),
}).strict();

export type QuestionSectionDetail = z.infer<typeof QuestionSectionDetailSchema>;

export const CreateQuestionSectionSchema = z
  .object({
    key: StableKeySchema,
    flow: QuestionFlowSchema,
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
    translations: QuestionSectionTranslationsSchema,
  })
  .strict();

export type CreateQuestionSectionRequest = z.infer<typeof CreateQuestionSectionSchema>;

/**
 * Updating a section.
 *
 * `key` is absent (immutable), and so is **`flow`**: moving a section between
 * registration and the wizard would move the lifecycle boundary underneath
 * members who are mid-flow — some would have "completed registration" by
 * answering questions that are now wizard questions, others the reverse. Staff
 * deactivate a section and create the replacement in the other flow, which is
 * visible and reversible.
 */
export const UpdateQuestionSectionSchema = z
  .object({
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
    translations: QuestionSectionTranslationsSchema.optional(),
  })
  .strict();

export type UpdateQuestionSectionRequest = z.infer<typeof UpdateQuestionSectionSchema>;

/** The whitelisted filters of the admin section list (N7). */
export const QuestionSectionsListQuerySchema = makeListQuery({
  flow: QuestionFlowSchema.optional(),
  isActive: BooleanParamSchema.optional(),
  search: z.string().min(1).max(200).optional(),
});

export type QuestionSectionsListQuery = z.infer<typeof QuestionSectionsListQuerySchema>;
